/**
 * CSV Generation for Advanced Calculator Data
 *
 * @description Generates full-year hourly calculation CSV for Advanced Calculator
 * with monthly-varying tariffs, consumption, and hourly patterns
 * @version 1.0.0
 * @created 2025-11-03
 * @author eCompanion2 Advanced Calculator Feature
 *
 * Purpose: Export detailed hourly calculation results for professional analysis
 * in Excel or other spreadsheet applications.
 *
 * Features:
 * - Full year of hourly data (8760 or 8784 rows)
 * - Monthly-varying tariffs and consumption
 * - All 6 tariffs: G11, G12, G12as, G12w, G13, G13s
 * - Energy and distribution costs per time zone
 * - Polish date/time formatting
 * - UTF-8 BOM for Excel compatibility
 * - Locale-safe number formatting
 */

import type {
  ProCalculatorFormValues,
  ProCalculatorResults,
  MonthlyConfiguration,
} from '../../types/proCalculator';
import { TAURON_2025_PRICING, VAT_RATE } from '../../../../amplify/data/utils/tariff-calculator';
import { isPolishHoliday } from '../holidays';

/**
 * Hourly Data Point (single hour of consumption and costs)
 */
interface HourlyDataPoint {
  date: Date;
  hour: number; // 0-23
  monthIndex: number; // 0-11
  consumption: number; // kWh
  dayType: 'weekday' | 'weekend' | 'holiday';

  // Costs for each tariff (PLN, gross)
  costs: {
    G11: { energy: number; distribution: number };
    G12: { energy: number; distribution: number; zone: string };
    G12as: { energy: number; distribution: number; zone: string };
    G12w: { energy: number; distribution: number; zone: string };
    G13: { energy: number; distribution: number; zone: string };
    G13s: { energy: number; distribution: number; zone: string };
  };
}

/**
 * Format number with 2 decimal places (for consumption)
 */
function formatConsumption(value: number): string {
  return value.toFixed(2);
}

/**
 * Format number with 4 decimal places (for costs)
 */
function formatCost(value: number): string {
  return value.toFixed(4);
}

/**
 * Format date as YYYY-MM-DD
 */
function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format hour as HH:00
 */
function formatHour(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

/**
 * Get day type label in Polish
 */
function getDayTypeLabel(dayType: 'weekday' | 'weekend' | 'holiday'): string {
  switch (dayType) {
    case 'weekday':
      return 'Dzień roboczy';
    case 'weekend':
      return 'Weekend';
    case 'holiday':
      return 'Święto';
    default:
      return 'Dzień roboczy';
  }
}

/**
 * Calculate hourly consumption distribution for a single month
 * Uses hourly pattern from monthly configuration
 */
function calculateMonthlyHourlyConsumption(
  month: MonthlyConfiguration,
  year: number,
  monthIndex: number,
  hourlyPatternFromBasic?: { weekday: number[]; weekend: number[] }
): number[] {
  // Total consumption for this month
  const monthlyTotal = month.consumption;

  // Get hourly pattern for this month
  let weekdayPattern: number[];
  let weekendPattern: number[];

  if (month.hourlyPattern === 'FROM_CSV_IMPORT' && hourlyPatternFromBasic) {
    // Use pattern from CSV import
    weekdayPattern = hourlyPatternFromBasic.weekday;
    weekendPattern = hourlyPatternFromBasic.weekend;
  } else {
    // Use predefined pattern for this month
    //
    // PHASE 2 ENHANCEMENT: Implement sophisticated pattern generators
    // Pattern types to implement:
    // - UNIFORM: Equal distribution across 24 hours (current MVP)
    // - MORNING_HEAVY: Peak consumption 6-10am
    // - EVENING_HEAVY: Peak consumption 5-10pm
    // - NIGHT_OWL: Higher consumption late evening/night
    // - WORK_FROM_HOME: Elevated daytime consumption
    // - SEASONAL: Month-specific patterns (heating/cooling)
    //
    // Phase 1 MVP: Uniform distribution used for all non-FROM_CSV_IMPORT patterns
    weekdayPattern = Array(24).fill(1 / 24);
    weekendPattern = Array(24).fill(1 / 24);
  }

  // Count weekdays and weekends in this month
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  let weekdayCount = 0;
  let weekendCount = 0;

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, monthIndex, day);
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const isHoliday = isPolishHoliday(date);

    if (isWeekend || isHoliday) {
      weekendCount++;
    } else {
      weekdayCount++;
    }
  }

  // Calculate daily consumption for weekdays and weekends
  // Total month consumption = (weekdayDaily * weekdayCount) + (weekendDaily * weekendCount)
  // Assume equal daily consumption for simplicity in MVP
  const dailyAverage = monthlyTotal / daysInMonth;

  // Build hourly consumption array for entire month (24 * daysInMonth)
  const hourlyConsumption: number[] = [];

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, monthIndex, day);
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const isHoliday = isPolishHoliday(date);

    const pattern = (isWeekend || isHoliday) ? weekendPattern : weekdayPattern;

    // Distribute daily consumption across 24 hours using pattern
    for (let hour = 0; hour < 24; hour++) {
      const hourlyConsumption_kWh = dailyAverage * pattern[hour];
      hourlyConsumption.push(hourlyConsumption_kWh);
    }
  }

  return hourlyConsumption;
}

/**
 * Calculate costs for all tariffs for a single hour
 */
function calculateHourlyCosts(
  hour: number,
  consumption: number,
  dayType: 'weekday' | 'weekend' | 'holiday',
  isHoliday: boolean
): HourlyDataPoint['costs'] {
  const pricing = TAURON_2025_PRICING;
  const isWeekendOrHoliday = dayType === 'weekend' || isHoliday;

  // G12 day/night hours
  const isG12Day = hour >= 6 && hour < 22;

  return {
    // G11 - Całodobowa (single zone)
    G11: {
      energy: consumption * (pricing.g11.energyPricePerKwh + (pricing.g11.qualityCharge || 0)) * VAT_RATE,
      distribution: consumption * pricing.g11.distributionPerKwh * VAT_RATE,
    },

    // G12 - Day/Night
    G12: {
      energy: isG12Day
        ? consumption * (pricing.g12.energyDayPricePerKwh + (pricing.g12.qualityCharge || 0)) * VAT_RATE
        : consumption * (pricing.g12.energyNightPricePerKwh + (pricing.g12.qualityCharge || 0)) * VAT_RATE,
      distribution: isG12Day
        ? consumption * pricing.g12.distributionDayPerKwh * VAT_RATE
        : consumption * pricing.g12.distributionNightPerKwh * VAT_RATE,
      zone: isG12Day ? 'Dzień' : 'Noc',
    },

    // G12as - Anti-smog
    G12as: {
      energy: isG12Day
        ? consumption * (pricing.g12as!.energyPricePerKwh + (pricing.g12as!.qualityCharge || 0)) * VAT_RATE
        : consumption * (pricing.g12as!.energyPricePerKwh + (pricing.g12as!.qualityCharge || 0)) * VAT_RATE,
      distribution: isG12Day
        ? consumption * pricing.g12as!.distributionDayPerKwh * VAT_RATE
        : consumption * pricing.g12as!.distributionNightPerKwh * VAT_RATE,
      zone: isG12Day ? 'Dzień' : 'Noc',
    },

    // G12w - Weekend
    G12w: {
      energy: isWeekendOrHoliday
        ? consumption * (pricing.g12w!.energyPricePerKwh + (pricing.g12w!.qualityCharge || 0)) * VAT_RATE
        : (isG12Day
          ? consumption * (pricing.g12w!.energyPricePerKwh + (pricing.g12w!.qualityCharge || 0)) * VAT_RATE
          : consumption * (pricing.g12w!.energyPricePerKwh + (pricing.g12w!.qualityCharge || 0)) * VAT_RATE),
      distribution: isWeekendOrHoliday
        ? consumption * pricing.g12w!.distributionWeekendPerKwh * VAT_RATE
        : (isG12Day
          ? consumption * pricing.g12w!.distributionDayPerKwh * VAT_RATE
          : consumption * pricing.g12w!.distributionNightPerKwh * VAT_RATE),
      zone: isWeekendOrHoliday ? 'Weekend' : (isG12Day ? 'Dzień' : 'Noc'),
    },

    // G13 - Peak/Off-peak
    G13: {
      energy: isWeekendOrHoliday
        ? consumption * (pricing.g13!.energyPricePerKwh + (pricing.g13!.qualityCharge || 0)) * VAT_RATE
        : ((hour >= 7 && hour < 13) || (hour >= 16 && hour < 21)
          ? consumption * (pricing.g13!.energyPricePerKwh + (pricing.g13!.qualityCharge || 0)) * VAT_RATE
          : consumption * (pricing.g13!.energyPricePerKwh + (pricing.g13!.qualityCharge || 0)) * VAT_RATE),
      distribution: isWeekendOrHoliday
        ? consumption * pricing.g13!.distributionOffPeakPerKwh * VAT_RATE
        : ((hour >= 7 && hour < 13) || (hour >= 16 && hour < 21)
          ? consumption * pricing.g13!.distributionPeakPerKwh * VAT_RATE
          : consumption * pricing.g13!.distributionOffPeakPerKwh * VAT_RATE),
      zone: isWeekendOrHoliday
        ? 'Pozaszczyt'
        : ((hour >= 7 && hour < 13) || (hour >= 16 && hour < 21) ? 'Szczyt' : 'Pozaszczyt'),
    },

    // G13s - Solar/Peak
    G13s: {
      energy: isWeekendOrHoliday
        ? consumption * (pricing.g13s!.energyPricePerKwh + (pricing.g13s!.qualityCharge || 0)) * VAT_RATE
        : ((hour >= 10 && hour < 15) || (hour >= 21 || hour < 7)
          ? consumption * (pricing.g13s!.energyPricePerKwh + (pricing.g13s!.qualityCharge || 0)) * VAT_RATE
          : consumption * (pricing.g13s!.energyPricePerKwh + (pricing.g13s!.qualityCharge || 0)) * VAT_RATE),
      distribution: isWeekendOrHoliday
        ? consumption * pricing.g13s!.distributionOffPeakPerKwh * VAT_RATE
        : ((hour >= 10 && hour < 15) || (hour >= 21 || hour < 7)
          ? consumption * pricing.g13s!.distributionOffPeakPerKwh * VAT_RATE
          : consumption * pricing.g13s!.distributionPeakPerKwh * VAT_RATE),
      zone: isWeekendOrHoliday
        ? 'Pozaszczyt solar'
        : ((hour >= 10 && hour < 15) || (hour >= 21 || hour < 7) ? 'Pozaszczyt solar' : 'Szczyt'),
    },
  };
}

/**
 * Generate hourly data points for entire year
 */
function generateYearlyHourlyData(
  formValues: ProCalculatorFormValues,
  hourlyPatternFromBasic?: { weekday: number[]; weekend: number[] }
): HourlyDataPoint[] {
  const year = formValues.calculationYear;
  const hourlyData: HourlyDataPoint[] = [];

  // Process each month
  for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
    const monthConfig = formValues.months[monthIndex];

    // Calculate hourly consumption for this month
    const monthlyHourlyConsumption = calculateMonthlyHourlyConsumption(
      monthConfig,
      year,
      monthIndex,
      hourlyPatternFromBasic
    );

    // Build hourly data points for this month
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    let hourIndex = 0;

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, monthIndex, day);
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const isHoliday = isPolishHoliday(date);

      const dayType: 'weekday' | 'weekend' | 'holiday' = isHoliday
        ? 'holiday'
        : (isWeekend ? 'weekend' : 'weekday');

      for (let hour = 0; hour < 24; hour++) {
        const consumption = monthlyHourlyConsumption[hourIndex];
        const costs = calculateHourlyCosts(hour, consumption, dayType, isHoliday);

        hourlyData.push({
          date,
          hour,
          monthIndex,
          consumption,
          dayType,
          costs,
        });

        hourIndex++;
      }
    }
  }

  return hourlyData;
}

/**
 * Generate CSV header row
 */
function generateCSVHeader(): string {
  const columns = [
    'Data',
    'Godzina',
    'Miesiąc',
    'Rodzaj dnia',
    'Zużycie (kWh)',
    // G11
    'G11 - Energia (zł)',
    'G11 - Dystrybucja (zł)',
    // G12
    'G12 - Strefa',
    'G12 - Energia (zł)',
    'G12 - Dystrybucja (zł)',
    // G12as
    'G12as - Strefa',
    'G12as - Energia (zł)',
    'G12as - Dystrybucja (zł)',
    // G12w
    'G12w - Strefa',
    'G12w - Energia (zł)',
    'G12w - Dystrybucja (zł)',
    // G13
    'G13 - Strefa',
    'G13 - Energia (zł)',
    'G13 - Dystrybucja (zł)',
    // G13s
    'G13s - Strefa',
    'G13s - Energia (zł)',
    'G13s - Dystrybucja (zł)',
  ];

  return columns.join(',');
}

/**
 * Generate CSV data row for a single hour
 */
function generateCSVRow(hourData: HourlyDataPoint): string {
  const { date, hour, monthIndex, consumption, dayType, costs } = hourData;

  const monthNames = [
    'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
    'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
  ];

  const rowData = [
    formatDate(date),
    formatHour(hour),
    monthNames[monthIndex],
    getDayTypeLabel(dayType),
    formatConsumption(consumption),
    // G11
    formatCost(costs.G11.energy),
    formatCost(costs.G11.distribution),
    // G12
    costs.G12.zone,
    formatCost(costs.G12.energy),
    formatCost(costs.G12.distribution),
    // G12as
    costs.G12as.zone,
    formatCost(costs.G12as.energy),
    formatCost(costs.G12as.distribution),
    // G12w
    costs.G12w.zone,
    formatCost(costs.G12w.energy),
    formatCost(costs.G12w.distribution),
    // G13
    costs.G13.zone,
    formatCost(costs.G13.energy),
    formatCost(costs.G13.distribution),
    // G13s
    costs.G13s.zone,
    formatCost(costs.G13s.energy),
    formatCost(costs.G13s.distribution),
  ];

  return rowData.join(',');
}

/**
 * Generate complete CSV file content for Advanced Calculator
 *
 * Creates full-year CSV with hourly data for all 6 tariffs.
 * Includes UTF-8 BOM for Excel compatibility.
 *
 * @param formValues - Advanced Calculator form values
 * @param results - Advanced Calculator results
 * @param hourlyPatternFromBasic - Optional hourly pattern from CSV import (for FROM_CSV_IMPORT pattern type)
 * @returns CSV file content as string
 *
 * @example
 * const csv = generateProCalculationCSV(formValues, results);
 */
export function generateProCalculationCSV(
  formValues: ProCalculatorFormValues,
  _results: ProCalculatorResults,
  hourlyPatternFromBasic?: { weekday: number[]; weekend: number[] }
): string {
  // Generate hourly data for the entire year
  const hourlyData = generateYearlyHourlyData(formValues, hourlyPatternFromBasic);

  // Start with UTF-8 BOM for Excel compatibility
  const BOM = '\uFEFF';

  // Generate header
  const header = generateCSVHeader();

  // Generate data rows
  const rows = hourlyData.map((hourData) => generateCSVRow(hourData));

  // Combine all parts
  const csvContent = BOM + header + '\n' + rows.join('\n');

  return csvContent;
}

/**
 * Default export
 */
export default {
  generateProCalculationCSV,
};
